import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Circle, HStack, Icon, SimpleGrid, Text, Tooltip, VStack } from "@chakra-ui/react";
import { ScheduledJobProto } from "build-ts/schedule";
import { Header } from "build-ts/metadata";
import showToast from "hooks/useCustomToast";
import { useChatContext } from "context/ChatContext";
import { useUIContext } from "context/UIContext";
import { useURLContext } from "context/URLContext";
import { useUser } from "context/UserContext";
import { HeaderWithStats } from "services/MetadataServices";
import { fetchAnalystList } from "services/serviceUtils";
import {
  createChatSession,
  fireNamingAgent,
  getAgentTypeFromEnv,
  loadScenarioJobs,
  normalizeWorkflowTagsForSend,
  useAutoSend,
} from "modules/chat/utils/chatUtils";
import { getRandomWelcomeMessage } from "utils/nameUtils";
import { AvatarWithMemo, rotateAnimation } from "components/navbar/Navbar";
import logo from "../../assets/logo_dark_small.png";
import MessageInputBox from "modules/chat/components/MessageInputBox";
import PromptButton from "modules/chat/components/PromptButton";
import { AppIcons } from "constants/icons";
import { PROMPT_BUTTONS } from "constants/prompts";
/**
 * New-chat entry point. Rendered for "/" and "/chat/new".
 *
 * Owns the welcome layout (animation + input + prompt buttons) directly —
 * no ChatPanel dependency. Calls initializeChatSession to persist the session
 * in DB, fires the naming agent, then navigates to /chat/:sessionId.
 *
 * ChatPage's URL effect detects isNewSession and skips the DB fetch.
 * Chat.tsx's auto-send effect calls continueSessionStreaming to send the first message.
 */
const NewChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, defaultAnalystId, user } = useUser();
  const { chatAnalystId } = useURLContext();
  const { getOrFetchAnalyst, setCurrentSessionHeader, setCurrentChatId } = useChatContext();
  const { setIsNavbarCollapsed } = useUIContext();

  // Navigation state: initialPrompt + analystId are set by workflow "Run" buttons
  const navState = location.state as { initialPrompt?: string; analystId?: string; preserveAnalyst?: boolean } | null;
  const initialPrompt = navState?.initialPrompt;
  const initialAnalystId = navState?.analystId;

  const [analystsList, setAnalystsList] = useState<HeaderWithStats[]>([]);
  const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);
  const [selectedAnalyst, setSelectedAnalyst] = useState<HeaderWithStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scenarioJobs, setScenarioJobs] = useState<ScheduledJobProto[]>([]);
  /** Prompt text of the currently hovered prompt button — shown as a preview in the input. */
  const [hoveredPrompt, setHoveredPrompt] = useState<string | null>(null);

  const [welcomeMessage] = useState(() => {
    const clientObject = JSON.parse(user?.client_object_json || "{}");
    const ankoUserName = clientObject?.ankoUserName || "";
    return getRandomWelcomeMessage(ankoUserName);
  });

  // Load analysts
  useEffect(() => {
    const load = async () => {
      setIsLoadingAnalysts(true);
      const list = await fetchAnalystList(false);
      setAnalystsList(list);
      const defaultAnalyst = list.find(a => a.id === defaultAnalystId) || list[0] || null;
      setSelectedAnalyst(defaultAnalyst);
      setIsLoadingAnalysts(false);
    };
    load();
  }, []);

  // Handle analystId from nav state (workflow Run/Edit buttons)

  useEffect(() => {
    if (!initialAnalystId || analystsList.length === 0) return;
    const analyst = analystsList.find(a => a.id === initialAnalystId);
    if (analyst) setSelectedAnalyst(analyst);
  }, [initialAnalystId, analystsList]);

  // Handle chatAnalystId URL param (AnalystPage Chat button)

  useEffect(() => {
    const urlHasChatAnalystId = location.search.includes("chatAnalystId");
    if (!urlHasChatAnalystId || !chatAnalystId) return;
    if (isLoadingAnalysts || analystsList.length === 0) return;

    const analyst = analystsList.find(a => a.id === chatAnalystId);
    if (analyst) {
      const prevInitialPrompt = navState?.initialPrompt;
      setSelectedAnalyst(analyst);
      navigate("/chat/new", { replace: true, state: { preserveAnalyst: true, initialPrompt: prevInitialPrompt } });
    } else {
      showToast("Analyst not found", "", "error");
      navigate("/chat/new", { replace: true });
    }
  }, [chatAnalystId, analystsList, isLoadingAnalysts]);

  // Eagerly fetch scenario jobs for the selected analyst so the @ mention menu
  // is populated before the user sends a message. Fire-and-forget — only affects the tag menu.
  useEffect(() => {
    if (!selectedAnalyst?.id) return;
    setScenarioJobs([]);
    loadScenarioJobs(selectedAnalyst.id).then(jobs => setScenarioJobs(jobs));
  }, [selectedAnalyst?.id]);

  // Main send handler
  const handleSendMessage = useCallback(
    async (
      inputMsg: string,
      _newChat: boolean,
      analyst: Header | null | undefined,
      csvFiles?: { file: File; fileName: string }[]
    ) => {
      const msgToSend = normalizeWorkflowTagsForSend(inputMsg.trim());
      if (!msgToSend && (!csvFiles || csvFiles.length === 0)) return;
      if (!analyst) {
        showToast("Please select an analyst first", "", "warning");
        return;
      }

      setIsLoading(true);
      setIsNavbarCollapsed(true);

      try {
        // Load full analyst proto and scenario jobs for the new session.
        const analystDetails = await getOrFetchAnalyst(analyst.id!);
        if (!analystDetails) {
          showToast("Failed to load analyst details", "", "error");
          return;
        }

        const scenarioJobs: ScheduledJobProto[] = await loadScenarioJobs(analystDetails.header?.id);
        const agentTypeFromEnv = getAgentTypeFromEnv();

        // Step 1: create and persist the session in Darwin DB.
        const sessionResponse = await createChatSession(userId!, analystDetails, scenarioJobs, agentTypeFromEnv, true);
        if (!sessionResponse) {
          showToast("Failed to create chat session", "", "error");
          return;
        }

        // Step 2: set ChatContext so it survives the navigation.
        setCurrentSessionHeader({ ...sessionResponse.session_header });
        setCurrentChatId(sessionResponse.session_header?.id ?? "");

        // Step 3: fire naming agent async — session is in DB now, no race condition.
        // Build a naming message that includes the user text (if any) plus one line
        // per attached file so the naming agent has full context even on file-only sends.
        const fileLines = (csvFiles ?? []).map(f => `[user added a file: ${f.fileName}]`).join("\n");
        const namingMsg = [msgToSend, fileLines].filter(Boolean).join("\n\n");
        if (namingMsg) {
          fireNamingAgent(namingMsg, sessionResponse.session_header?.id ?? "", userId!, title => {
            setCurrentSessionHeader(prev => (prev ? { ...prev, name: title } : prev));
          });
        }

        // Step 4: navigate — Chat.tsx auto-send will continue the already-created session.
        navigate(`/chat/${sessionResponse.session_header?.id ?? ""}`, {
          replace: true,
          state: {
            isNewSession: true,
            sessionId: sessionResponse.session_header?.id ?? "",
            pendingMessage: msgToSend,
            // File objects survive in-memory navigation; Chat.tsx auto-send reads
            // these and passes them to continueSessionStreaming on the first turn.
            pendingCsvFiles: csvFiles,
            scenarioJobs,
            analystId: analystDetails.header?.id,
          },
        });
      } catch (error) {
        console.error("Error preparing chat session:", error);
        showToast("Failed to start chat", "", "error");
      } finally {
        setIsLoading(false);
      }
    },
    [userId, getOrFetchAnalyst, setCurrentSessionHeader, setCurrentChatId, setIsNavbarCollapsed, navigate]
  );

  // Analyst change (updates selection in place)
  const handleAnalystChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const analyst = analystsList.find(a => a.id === e.target.value);
      setSelectedAnalyst(analyst || null);
    },
    [analystsList]
  );

  // Auto-send initialPrompt when analysts are ready (e.g. workflow "Run" button).
  useAutoSend(
    initialPrompt,
    !isLoading && !!selectedAnalyst && (!initialAnalystId || selectedAnalyst.id === initialAnalystId),
    useCallback(prompt => handleSendMessage(prompt, true, selectedAnalyst), [handleSendMessage, selectedAnalyst])
  );

  // Render

  return (
    <Box position="relative" w="100%" h="100%" display="flex" flexDirection="column" bg="bg.surface">
      <VStack flex={1} w="100%" h="100%" justify="center" align="center" spacing={9} mt={-3}>
        <HStack spacing={5}>
          <Circle bg="purple.50" p={2}>
            <AvatarWithMemo src={logo} animation={`${rotateAnimation} 3s linear infinite`} name="Anko" size="md" />
          </Circle>
          <Text color="gray.700" fontSize="3xl" fontWeight="400" m={0}>
            {welcomeMessage}
          </Text>
        </HStack>
        <MessageInputBox
          chatId={undefined}
          sessionId={undefined}
          isLoading={isLoading}
          isLoadingChat={false}
          isInputDisabledDueToEva={false}
          isBlockedByAnalystLoading={isLoadingAnalysts}
          selectedAnalyst={selectedAnalyst}
          analystsList={analystsList}
          isLoadingAnalysts={isLoadingAnalysts}
          isFromEva={false}
          onAnalystChange={handleAnalystChange}
          onSendMessage={handleSendMessage}
          previewText={hoveredPrompt ?? undefined}
          defaultMessage={initialPrompt ?? undefined}
          hasMessages={false}
          width="700px"
          scenarioJobs={scenarioJobs}
        />
        {/* Prompt buttons */}
        <VStack spacing={3} align="flex-start" maxW="700px" w="100%">
          <HStack
            display={{ base: "none", md: "flex" }}
            flexShrink={0}
            color="purple.600"
            fontSize="sm"
            fontWeight="medium"
          >
            <Icon as={AppIcons.sparkles} boxSize={4} />
            <Text m={0}>Try these</Text>
          </HStack>
          <SimpleGrid columns={2} spacing={2} w="100%">
            {PROMPT_BUTTONS.map(btn => (
              <Tooltip
                key={btn.title}
                label="Analyst is loading, please wait"
                isDisabled={!isLoadingAnalysts}
                hasArrow
                placement="top"
                shouldWrapChildren
              >
                <PromptButton
                  icon={<Icon as={AppIcons[btn.iconKey]} boxSize={5} />}
                  title={btn.title}
                  description={btn.helpText}
                  isDisabled={isLoadingAnalysts}
                  onMouseEnter={() => setHoveredPrompt(btn.prompt)}
                  onMouseLeave={() => setHoveredPrompt(null)}
                  onClick={() => handleSendMessage(btn.prompt, true, selectedAnalyst)}
                  w="100%"
                />
              </Tooltip>
            ))}
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  );
};

export default NewChatPage;
